import { createContext, useContext, useEffect, useRef } from "react";

/**
 * DBCO-0062 (N-10) - the one way a panel, drawer or dialog tells the operation page to stop refreshing while it is open.
 * A holder calls {@link useGatedOpen} with its open flag (or `register()` for imperative use); the detail query is paused
 * while `busy`. Outside a provider (the Add Operation page, which never polls) it does nothing.
 */
export interface PollingGate {
  /** True while at least one holder is open. */
  busy: boolean;
  /** Marks one holder open; the returned function marks it closed again. */
  register: () => () => void;
}

export const INERT_GATE: PollingGate = { busy: false, register: () => () => undefined };

export const PollingGateContext = createContext<PollingGate>(INERT_GATE);

export const usePollingGate = (): PollingGate => useContext(PollingGateContext);

/** Keeps the gate busy while `open` is true (and releases it on close and on unmount). */
export const useGatedOpen = (open: boolean): void => {
  const { register } = usePollingGate();
  const release = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    release.current = register();
    return () => {
      release.current?.();
      release.current = null;
    };
  }, [open, register]);
};
